import { ZfBarChart, ZfStack } from '@zafire/react';
import type Highcharts from 'highcharts/highstock';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWealth } from '../../../../api';
import { useWealthContext } from '../../../../contexts/wealth-context';
import { useYwuApp } from '../../../../contexts/ywu-app-context';
import { getSeriesWeight } from '../../../../utils/chartUtils';
import { getBarChartOptions, seriesStates } from '../barChartOptions';
import { EmptyChartMessage } from '../EmptyChartMessage';
import { chartAssetColors, chartLiabilityColors } from '../pieChartOptions';
import { IncomeExpensesBarLegend } from './IncomeExpensesBarLegend';
import type { IncomeExpensesBarLegendEntry, IncomeExpensesBarStackPoint } from './IncomeExpensesBarTypes';

const EMPTY_STACK_POINTS: IncomeExpensesBarStackPoint[] = [];

export const IncomeExpensesChart: FunctionComponent = () => {
    const { t } = useTranslation();

    const { activeLanguage: language } = useYwuApp();
    const { clientCurrency, wealthChartDataMap } = useWealthContext();
    const { expensesTotal, incomeTotal } = useWealth();
    const [hiddenSeries, setHiddenSeries] = useState<Set<number>>(() => new Set());

    const incomeChartConfig = wealthChartDataMap['INCOME'];
    const expensesChartConfig = wealthChartDataMap['EXPENSE'];
    const biggestTotalAmount = incomeTotal > expensesTotal ? incomeTotal : expensesTotal;

    const incomeSeries: IncomeExpensesBarStackPoint[] | undefined = incomeChartConfig?.visibleCategories.map((category, index) => ({
        x: 0,
        name: category.name[language],
        y: category.totalAmountCurrentYear,
        color: chartAssetColors[index],
        weight: getSeriesWeight(category.ratioTotalAccountCurrentYear, biggestTotalAmount, category.totalAmountCurrentYear, incomeChartConfig.netAmount),
    }));

    const expenseSeries: IncomeExpensesBarStackPoint[] | undefined = expensesChartConfig?.visibleCategories.map((category, index) => ({
        x: 1,
        name: category.name[language],
        y: category.totalAmountCurrentYear,
        color: chartLiabilityColors[index],
        weight: getSeriesWeight(category.ratioTotalAccountCurrentYear, biggestTotalAmount, category.totalAmountCurrentYear, expensesChartConfig.netAmount),
    }));

    if (expenseSeries && expensesChartConfig && expensesChartConfig.netAmount > 0) {
        expenseSeries.push({
            color: '#E5E5E5',
            name: t(expensesChartConfig.netTitleTranslationKey),
            weight: expensesChartConfig.netRatio,
            x: 1,
            y: expensesChartConfig.netAmount,
        });
    } else if (incomeSeries && incomeChartConfig && incomeChartConfig.netAmount > 0) {
        incomeSeries.push({
            color: '#E5E5E5',
            name: t(incomeChartConfig.netTitleTranslationKey),
            weight: incomeChartConfig.netRatio,
            x: 0,
            y: incomeChartConfig.netAmount,
        });
    }

    const incomePoints = incomeSeries ?? EMPTY_STACK_POINTS;
    const expensePoints = expenseSeries ?? EMPTY_STACK_POINTS;

    const { incomeLegendEntries, expenseLegendEntries } = useMemo(() => {
        const toLegendEntries = (points: IncomeExpensesBarStackPoint[], offset: number): IncomeExpensesBarLegendEntry[] =>
            points
                .map((p, idx) => ({
                    seriesIndex: offset + idx,
                    name: p.name,
                    color: p.color,
                }))
                .filter((p): p is IncomeExpensesBarLegendEntry => Boolean(p.name) && Boolean(p.color));

        return {
            incomeLegendEntries: toLegendEntries(incomePoints, 0),
            expenseLegendEntries: toLegendEntries(expensePoints, incomePoints.length),
        };
    }, [incomePoints, expensePoints]);

    const onToggleSeries = useCallback((seriesIndex: number) => {
        setHiddenSeries((prev) => {
            const next = new Set(prev);
            if (next.has(seriesIndex)) next.delete(seriesIndex);
            else next.add(seriesIndex);

            return next;
        });
    }, []);

    const series: Highcharts.SeriesOptionsType[] = useMemo(
        () =>
            incomePoints.concat(expensePoints).map((point, seriesIndex) => ({
                pointWidth: 50,
                type: 'bar',
                data: [point],
                color: point.color,
                name: point.name,
                states: seriesStates,
                showInLegend: !!point.name,
                visible: !hiddenSeries.has(seriesIndex),
            })),
        [incomePoints, expensePoints, hiddenSeries],
    );

    const baseOptions = useMemo(
        () =>
            getBarChartOptions({
                currency: clientCurrency ?? '',
                series: series ?? [],
                xAxis: {
                    categories: [t(incomeChartConfig?.titleTranslationKey ?? ''), t(expensesChartConfig?.titleTranslationKey ?? '')],
                },
            }),
        [clientCurrency, series, t, incomeChartConfig?.titleTranslationKey, expensesChartConfig?.titleTranslationKey],
    );

    const options: Highcharts.Options = useMemo(() => ({ ...baseOptions, legend: { ...baseOptions.legend, enabled: false } }), [baseOptions]);

    if (!incomeChartConfig?.totalAmount && !expensesChartConfig?.totalAmount) return <EmptyChartMessage />;

    return (
        <ZfStack direction="column" alignment="center" spacing="medium">
            <ZfBarChart options={options} />
            <IncomeExpensesBarLegend
                incomeEntries={incomeLegendEntries}
                expenseEntries={expenseLegendEntries}
                hiddenSeries={hiddenSeries}
                onToggleSeries={onToggleSeries}
            />
        </ZfStack>
    );
};
